import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {CustomerContact, InviteAuthService, isSameRef, SigningPackage, UserAuthService, UserService} from '@lifeislife/lifeislife-domain';
import {debounceTime, distinctUntilChanged, filter, map, publishReplay, refCount, switchMap, take} from 'rxjs/operators';
import {CustomerContextService} from '../service/customer-context-service';
import {ActivatedRoute, Router} from '@angular/router';
import {NavigationService} from '../service/navigation-service';
import {AppHomeScreenInstallerService, WebPushRegistrationService} from '@lifeislife/lifeislife-components';
import {ShellBannerDesc} from './shell-banners/shell-banner-desc';
import {ShellBannerService} from '../service/shell-banner-service';
import {PendingSigningPackageService} from '../service/pending-signing-package.service';

@Component({
  selector: 'cf-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss'],
})
export class AppShellComponent implements OnInit, OnDestroy {

  routeActive: boolean;
  authenticated$: Observable<boolean>;
  customerContactSet$: Observable<boolean>;
  customerPasswordSet$: Observable<boolean>;
  customerPasswordClosed$ = new BehaviorSubject<boolean>(true);

  routeContentVisible$: Observable<boolean>;
  userPasswordModalVisible$: Observable<boolean>;
  contactSelectionModalVisible$: Observable<boolean>;


  private subscription: Subscription;

  constructor(
    private userAuthService: UserAuthService,
    private userService: UserService,
    private customerContextService: CustomerContextService,
    private userInviteAuthService: InviteAuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
    private shellBannerService: ShellBannerService,
    private appInstallationService: AppHomeScreenInstallerService,
    private webpushNotificationService: WebPushRegistrationService,
    private pendingSigningPackageService: PendingSigningPackageService,
  ) {
  }

  ngOnInit(): void {
    this.subscription = new Subscription();
    //
    // // Check if we are on a valid route, otherwise redirect to products.
    // // This replace the route definitions redirects (redirectTo: '/login'), allowing to keep the
    // // auth code query param
    // // You cannot trigger a navigation to / then
    // const routeSubscription = this.activatedRoute.url.pipe(
    //   filter(f => f == null || f.length < 1),
    // ).subscribe(s => {
    //   console.warn('Navigated to invalid route: ' + s);
    //   this.router.navigate(['/products'], {
    //     queryParamsHandling: 'preserve',
    //   });
    // }, e => console.warn(e));
    // this.subscription.add(routeSubscription);


    this.authenticated$ = this.userAuthService.state$.pipe(
      map(s => s != null && s.status === 'authenticated'),
      publishReplay(1), refCount(),
    );

    // Generic query params in generated links:
    // passwordReset: If user has reset his password, this query param is added by backend besides auth code
    const routeSnapshotQueryParamMap = this.activatedRoute.snapshot.queryParamMap;
    const setPassword = routeSnapshotQueryParamMap.get('passwordReset');
    if (setPassword) {
      this.customerPasswordClosed$.next(false);
    }
    // customerContactId: to activate a specific customer contact context
    const customerContactIdParam = routeSnapshotQueryParamMap.get('customerContactId');
    if (customerContactIdParam) {
      this.customerContextService.activateCustomerContactContext(customerContactIdParam);
    }
    // customerId: to activate a specific customer context
    const customerIdParam = routeSnapshotQueryParamMap.get('customerId');
    if (customerIdParam) {
      this.customerContextService.activateCustomerContext(customerIdParam);
    }

    this.customerContactSet$ = this.customerContextService.hasActiveCustomerContact$;
    // Aim is to check once on login, then if user set password but it didn't work, fail silently
    this.customerPasswordSet$ = this.authenticated$.pipe(
      distinctUntilChanged(),
      switchMap(a => this.checkPasswordSetIfAuthenticated$(a)),
      publishReplay(1), refCount(),
    );

    this.routeContentVisible$ = combineLatest([
      this.authenticated$, this.customerContactSet$, this.customerPasswordSet$, this.customerPasswordClosed$,
      this.navigationService.routeProtected$,
    ]).pipe(
      debounceTime(200),
      map(r => this.isRouteContentVisible(...r)),
      publishReplay(1), refCount(),
    );
    this.userPasswordModalVisible$ = combineLatest([
      this.authenticated$, this.customerContactSet$, this.customerPasswordSet$, this.customerPasswordClosed$,
      this.navigationService.routeProtected$,
    ]).pipe(
      debounceTime(200),
      map(r => this.isCustomerPasswordContentVisible(...r)),
      publishReplay(1), refCount(),
    );
    this.contactSelectionModalVisible$ = combineLatest([
      this.authenticated$, this.customerContactSet$, this.customerPasswordSet$, this.customerPasswordClosed$,
      this.navigationService.routeProtected$,
    ]).pipe(
      debounceTime(200),
      map(r => this.isContactSelectioneContentVisible(...r)),
      publishReplay(1), refCount(),
    );

    // const inviteSubscription = this.userInviteAuthService.attemptRestoreFromQueryString$(Role.CUSTOMER).pipe(
    //   filter(user => user != null),
    //   catchError(e => EMPTY),
    //   switchMap(s => this.userAuthService.getNextUserOrNull$()),
    // ).subscribe(() => this.router.navigate(['/products'], {
    //   replaceUrl: true,
    // }));
    // this.subscription.add(inviteSubscription);

    // Banner pre-asking app installation
    this.appInstallationService.canInstall$.pipe(
      filter(canInstall => canInstall === true),
      take(1),
    ).subscribe(i => this.createAppInstallBanner());

    this.customerContextService.activeCustomerContact$.pipe(
      filter(c => c != null && c.contactRef != null),
      distinctUntilChanged(isSameRef),
    ).subscribe(c => this.subscribeWebPushNotifications(c));

    this.pendingSigningPackageService.getPendingDraft$().pipe(
      filter(sp => sp != null),
    ).subscribe(i => this.createSigningPackageBanner(i));

  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onActivateRoute(event: any) {
    this.routeActive = true;
  }

  onDeactiveRoute(event: any) {
    this.routeActive = false;
  }

  onPasswordResetSuccess() {
    this.customerPasswordClosed$.next(true);
    const queryparams = this.activatedRoute.snapshot.queryParamMap;
    const setPassword = queryparams.get('passwordReset');
    if (setPassword) {
      this.router.navigate(['.'], {
        queryParams: {},
        replaceUrl: true,
        relativeTo: this.activatedRoute,
      });
    }
  }

  onPasswordResetCancel() {
    this.customerPasswordClosed$.next(true);
  }

  private isRouteContentVisible(authenticated: boolean, customerContextSet: boolean,
                                customerPasswordSet: boolean, customerPasswordClosed: boolean,
                                routeProtected: boolean) {
    if (!authenticated || !routeProtected) {
      return true;
    }
    if (!customerContextSet || (!customerPasswordSet && !customerPasswordClosed)) {
      return false;
    }
    return true;
  }

  private isCustomerPasswordContentVisible(authenticated: boolean, customerContextSet: boolean,
                                           customerPasswordSet: boolean, customerPasswordClosed: boolean,
                                           routeProtected: boolean) {
    if (!authenticated || !routeProtected) {
      return false;
    }
    if (!customerPasswordSet && !customerPasswordClosed) {
      return true;
    }
    return false;
  }

  private isContactSelectioneContentVisible(authenticated: boolean, customerContextSet: boolean,
                                            customerPasswordSet: boolean, customerPasswordClosed: boolean,
                                            routeProtected: boolean) {
    if (!authenticated || !routeProtected) {
      return false;
    }
    if (!customerPasswordSet && !customerPasswordClosed) {
      return false;
    }
    if (!customerContextSet) {
      return true;
    }
    return false;
  }

  private checkPasswordSetIfAuthenticated$(autenticated: boolean) {
    if (!autenticated) {
      return of(false);
    }
    return this.userService.isLoggedUserPasswordSet$();
  }

  private createAppInstallBanner() {
    const bannerDesc: ShellBannerDesc = {
      key: ShellBannerService.BANNER_ID_APP_INSTALL,
      message: `Vous pouvez installer My LifeisLife sur votre écran d'accueil`,
      actions: [{
        key: 'cancel',
        message: `Ignorer`,
        clearMessage: true,
        callback: () => this.appInstallationService.discardAppInstall(),
      }, {
        key: 'install',
        message: `Installer`,
        clearMessage: true,
        callback: () => this.appInstallationService.askInstallToHomeScreen(),
      }],
    };
    this.shellBannerService.addBanner(bannerDesc);
  }

  private createNotificationSubscribeBanner() {
    const bannerDesc: ShellBannerDesc = {
      key: ShellBannerService.BANNER_ID_WEBPUSH_SUBCRIBE,
      message: `Acceptez les notifications pour rester informé de l'évolution de vos produits`,
      actions: [{
        key: 'cancel',
        message: `Ignorer`,
        clearMessage: true,
        callback: () => this.webpushNotificationService.discard(),
      }, {
        key: 'ok',
        message: `Activer les notifications`,
        clearMessage: true,
        callback: () => this.subscribeToWebPush(),
      }],
    };
    this.shellBannerService.addBanner(bannerDesc);
  }

  private createSigningPackageBanner(signingPackage: SigningPackage) {
    const signerUrl = this.pendingSigningPackageService.getSignerUrl(signingPackage);
    if (signerUrl == null) {
      return;
    }
    const bannerDesc: ShellBannerDesc = {
      key: ShellBannerService.BANNER_ID_SIGNING_PACKAGE_PENDING,
      message: `Vous avez des documents à signer: '${signingPackage.publicDescription}'`,
      actions: [{
        key: 'sign',
        message: `Les signer`,
        clearMessage: true,
        callback: () => this.navigationService.navigateToUrl(signerUrl),
      }],
    };
    this.shellBannerService.addBanner(bannerDesc);
  }

  private subscribeWebPushNotifications(customerContact: CustomerContact) {
    if (customerContact == null || customerContact.contactRef == null) {
      return;
    }

    // Resubscribe silently to notify device usage to backend
    this.webpushNotificationService.hasSubscribe$().pipe(
      debounceTime(1000),
      take(1),
      filter(has => has === true),
      switchMap(has => this.webpushNotificationService.subscribeToNotifications$(customerContact.contactRef)),
    ).subscribe();


    // Display the permission banner otherwise
    this.webpushNotificationService.canSubscribe$().pipe(
      debounceTime(1000),
      take(1),
      filter(can => can === true),
    ).subscribe(i => this.createNotificationSubscribeBanner());

  }

  private subscribeToWebPush() {
    const contactRef = this.customerContextService.getActiveContactRef();
    if (contactRef) {
      this.webpushNotificationService.subscribeToNotifications$(contactRef).subscribe();
    }
  }
}

